/**
 * © 2025 Grupo Salamander SAPI de CV
 * Author: Pablo Adoue Peralta
 *
 * UIButtonIcon: Renders a Material UI Button with a single icon and navigation/click logic.
 */

import { Button, CircularProgress } from '@mui/material'
import type { TUIButtonIcon } from './types'
import { useClickHandler, renderIconWithProps } from './utils'
import { ICON_BUTTON_STYLES, DEFAULT_TYPE } from './constants'

/**
 * Icon button component that displays a single icon with optional navigation.
 * Supports onClick callbacks and external links (href).
 */
export default function UIButtonIcon({
  loading,
  variant,
  disabled,
  color,
  size,
  type = DEFAULT_TYPE,
  icon,
  tooltip,
  testId,
  active,
  tabRole = true,
  onClick,
  href,
}: TUIButtonIcon) {
  const isDisabled = !!disabled || !!loading
  const handleClick = useClickHandler({ onClick, href }, isDisabled)

  const isTab = variant === 'tab'
  const isTabRole = isTab && tabRole

  return (
    <Button
      data-testid={testId}
      loading={!!loading}
      variant={isTab ? 'text' : variant}
      disabled={isDisabled}
      type={type}
      size={size}
      disableRipple
      color={color}
      className={
        isTab
          ? `UIButton-tab${active ? ' UIButton-tab-active' : ''}`
          : undefined
      }
      // See `UIButtonLabel.tsx` — same `UITabs` tablist contract.
      role={isTabRole ? 'tab' : undefined}
      aria-selected={isTabRole ? !!active : undefined}
      aria-busy={loading || undefined}
      onClick={handleClick}
      sx={ICON_BUTTON_STYLES}
      aria-label={tooltip}
    >
      {loading ? (
        // `aria-label`/`aria-busy` above already name and state this
        // button — an unlabelled nested `role="progressbar"` is a
        // redundant node to a screen reader (jest-axe
        // `aria-progressbar-name`, plan task 5.3 part 1).
        <CircularProgress aria-hidden="true" size={16} color="inherit" />
      ) : (
        renderIconWithProps(icon, { fontSize: 'small' })
      )}
    </Button>
  )
}
